/**
 * 接口域名（服务）注册表 + 环境切换
 *
 * 背景：原来每个接口域名一份 axios 封装（src/lib/axios.js 主接口、src/lib/opAxios.js op 接口），
 * 各自 axios.create、各自往「全局 axios」挂拦截器，域名也写死在文件里 —— 加一个新域名就得
 * 再抄一份封装，且两套拦截器会互相叠加。
 *
 * 现在收敛成一张表：新增一个域名只需要
 *   1）在下面的 API_URLS 里给每个环境加一项；
 *   2）用 createRequest('新服务名', { envelope, auth }) 生成请求对象
 *      （见 src/lib/request/createRequest.js、src/lib/request/index.js）。
 * 请求实现只有一处出口：src/lib/request/createHttpService.js。
 *
 * 环境怎么定（resolveApiEnv）：
 *   - 正式域名（pcmall / www …）以及其它"不可切换"的宿主 → 永远 prod，不会被 URL 参数改坏；
 *   - 可切换宿主：本地开发（npm run serve）、测试域名（pcmall-test / test-pc）、
 *     局域网预览地址（192.168.110.*，方便用手机连本机看构建产物）→ 默认 DEFAULT_ENV
 *     （其中"局域网预览 + 非开发模式"默认给 prod，避免把正式包的预览默认打到测试/预发布）。
 * 切换方式（地址栏加参数，会记进 localStorage，刷新/换页都不丢）：
 *   ?api=prod   正式
 *   ?api=pre    预发布
 *   ?api=test   测试
 *   ?api=local  本地（本机后端，地址见 LOCAL_BASEURL）
 *   ?api=reset  清掉覆盖，回到默认档
 * 参数生效后会自动从地址里去掉并重载，方便继续操作。
 *
 * 用法：
 *   import { getServiceBaseURL } from '@/config/api';
 *   getServiceBaseURL('op')  // 当前环境下 op 接口的 baseURL（每次请求都会重新取）
 */
import conf from '@/config';

/** 本地后端地址：?api=local / 悬浮切换器的「本地」档使用（主接口与 op 同域名） */
const LOCAL_BASEURL = 'http://192.168.110.46';

const IS_DEV = process.env.NODE_ENV === 'development';
/** 预发布 op（apiv2）网关；本地开发走同源 '/newapi'，由 vue.config.js 代理到这里 */
const OP_CANARY = 'https://apiv2-canary.pandayoufu.com';
/** 测试 op（apiv2）网关：直连（本地也直连，不借用已指向 canary 的那条 '/newapi' 代理） */
const OP_TEST = 'https://test-api-v2.pandayoufu.com/apiv2';

/**
 * 默认档：可切换宿主（本地开发 / 测试域名）不带 ?api= 时走这一档，想换只改这一行。
 * 现默认「测试」：右下角悬浮切换器开箱即显示「接口：测试」；
 * 正式域名不受影响（resolveApiEnv 在不可切换宿主上恒为 prod）。
 */
const DEFAULT_ENV = 'test';

/** 正式站点（部署在正式域名上的包）：只有这里才有「/newapi → apiv2」的 nginx 转发规则 */
function isProdSite() {
  return !canSwitchApiEnv();
}

/**
 * 「环境 × 服务」地址表 —— 加环境 / 改域名只动这一块
 *
 * 三点事实（2026-10 实测）：
 *  1）主接口没有独立的预发布域名（apiv2-canary 只服务 apiv2；pre-api / api-canary /
 *     pre-pc / pcmall-pre 均不存在），所以 pre 与 test 的主接口是同一个网关；
 *  2）电影（/v1/ju/movies/*）与景点（/v2、/v1/ctrip/* 的目录类接口）数据只配在正式，
 *     测试/预发布那一档没有 → 这两域的 pre 档也指正式，否则页面直接没数据；
 *  3）'/newapi' 是"正式站点上的同源路径"，本地 / 局域网预览 / 测试站点都没有这条转发规则
 *     （本地 devServer 的 '/newapi' 代理指向 canary），这些宿主用同源路径会拿到 canary 的
 *     op 数据 —— 与"正式"档不符，所以它们直连正式 apiv2（实测这两种源的 CORS 预检均放行）。
 */
export const API_URLS = {
  // 正式
  prod: {
    main: conf.api.prod,
    op: isProdSite() ? '/newapi' : 'https://apiv2.pandayoufu.com',
    movie: conf.api.prod,
    tour: conf.api.prod
  },
  // 预发布（主接口与测试同网关；op 用 canary）
  pre: {
    main: conf.api.test,
    op: IS_DEV ? '/newapi' : OP_CANARY,
    movie: conf.api.prod,
    tour: conf.api.prod
  },
  // 测试
  test: {
    main: conf.api.test,
    op: OP_TEST,
    movie: conf.api.test,
    tour: conf.api.test
  },
  // 本地（本机跑的后端；op 与主接口同域名）
  local: {
    main: LOCAL_BASEURL,
    op: LOCAL_BASEURL,
    movie: LOCAL_BASEURL,
    tour: LOCAL_BASEURL
  }
};

/** 可选环境（?api= 的取值，外加 reset） */
export const API_ENVS = ['prod', 'pre', 'test', 'local'];

/** 覆盖值在 localStorage 里的 key */
const ENV_KEY = 'pc_api_env';

/** 局域网预览地址（如 192.168.110.46:8080）：也可以切环境，方便手机连本机调试构建产物 */
function isLanHost() {
  return window.location.hostname.indexOf('192.168.110.') === 0;
}

/** 测试站点（部署在测试域名上的包）：不给「本地」档（那是开发机地址），也不给「回默认档」 */
export function isTestHost() {
  const hostname = window.location.hostname;
  return (
    hostname === 'pcmall-test.pandayoufu.com' ||
    hostname === 'test-pc.pandayoufu.com'
  );
}

/**
 * 是否处于"可切换"的宿主：本地开发 / 测试域名 / 局域网预览；其余（含正式）一律锁 prod。
 * 悬浮切换器也按这个判定决定显不显示。
 */
export function canSwitchApiEnv() {
  return IS_DEV || isLanHost() || isTestHost();
}

/**
 * 当前宿主允许切换的档位（含 'reset' 表示允许"回默认档"）：
 *   - 本地开发 / 局域网预览：正式 / 预发布 / 测试 / 本地 + reset
 *   - 测试站点：只有 正式 / 预发布 / 测试
 * 悬浮切换器的选项列表与 ?api= 的取值校验都用它，保证"看不到的档也切不过去"。
 */
export function getSelectableEnvs() {
  if (isTestHost()) return ['prod', 'pre', 'test'];
  return API_ENVS.concat('reset');
}

/** 读 localStorage 里的覆盖值（隐私模式等场景下失败就当作没有） */
function readSavedEnv() {
  try {
    return window.localStorage.getItem(ENV_KEY) || '';
  } catch (err) {
    return '';
  }
}

/**
 * URL 参数切环境：?api=prod|pre|test|local|reset
 * 写进 localStorage 后去掉参数并重载（请求层每次请求都会取 baseURL，重载只为让页面数据一并刷新）。
 * 正式域名下不生效 —— 既不读也不写覆盖。
 */
function applyEnvFromUrl() {
  let wanted = '';
  try {
    wanted = new URL(window.location.href).searchParams.get('api') || '';
  } catch (err) {
    return;
  }
  if (!wanted) return;
  if (!canSwitchApiEnv()) {
    // eslint-disable-next-line no-console
    console.warn(
      `[config/api] 当前宿主的接口环境固定为 prod，忽略 ?api=${wanted}`
    );
    return;
  }
  const valid = getSelectableEnvs();
  if (valid.indexOf(wanted) === -1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[config/api] ?api=${wanted} 在当前宿主不可用，可选：${valid.join(' / ')}`
    );
    return;
  }
  try {
    if (wanted === 'reset') {
      window.localStorage.removeItem(ENV_KEY);
    } else {
      window.localStorage.setItem(ENV_KEY, wanted);
    }
  } catch (err) {
    /* 写不进去（隐私模式等）：忽略，本次仅提示 */
  }
  // eslint-disable-next-line no-console
  console.info(
    `[config/api] 接口环境已切到 ${
      wanted === 'reset' ? DEFAULT_ENV : wanted
    }，正在刷新…`
  );
  const url = new URL(window.location.href);
  url.searchParams.delete('api');
  window.location.replace(url.toString());
}

applyEnvFromUrl();

/** 写 / 清覆盖值；写不进去（隐私模式等）返回 false */
function saveEnv(env) {
  try {
    if (env === 'reset') window.localStorage.removeItem(ENV_KEY);
    else window.localStorage.setItem(ENV_KEY, env);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * 切换接口环境并重载页面（供悬浮切换器使用；?api= 参数那条路见 applyEnvFromUrl）
 * @param {'prod'|'pre'|'test'|'local'|'reset'} env
 * @returns {boolean} 是否已发起切换（正式域名下直接返回 false，不做任何修改）
 */
export function setApiEnv(env) {
  if (!canSwitchApiEnv()) return false;
  if (getSelectableEnvs().indexOf(env) === -1) return false;
  if (!saveEnv(env)) return false;
  window.location.reload();
  return true;
}

/**
 * 当前接口环境
 * @returns {'prod'|'pre'|'test'|'local'}
 */
export function resolveApiEnv() {
  if (!canSwitchApiEnv()) return 'prod';
  const saved = readSavedEnv();
  if (API_ENVS.indexOf(saved) !== -1) return saved;
  // 没手动切过时的默认档：局域网预览（非开发模式）默认 prod，其余可切换宿主走 DEFAULT_ENV
  return !IS_DEV && isLanHost() ? 'prod' : DEFAULT_ENV;
}

/**
 * 取指定服务的 baseURL（请求层每次请求都会调用，所以切换环境后不需要重新构建）
 * @param {string} service 服务名（见 API_URLS），默认主接口
 * @returns {string}
 */
export function getServiceBaseURL(service = 'main') {
  const env = resolveApiEnv();
  // 注意：这里不能用 `|| 兜底` 当成"没配" —— 同源相对路径（如 op 的 '/newapi'、
  // 本地代理用的 ''）都是有效值，空串不等于没配
  const url = (API_URLS[env] || {})[service];
  if (url !== undefined && url !== null) return url;
  // 没登记的服务名 / 环境：早点暴露，而不是静默请求到错误域名
  // eslint-disable-next-line no-console
  console.warn(`[config/api] 未登记的接口服务：${service}（当前环境 ${env}）`);
  return (API_URLS.prod || {})[service] || '';
}

// eslint-disable-next-line no-console
console.info(
  `[config/api] 接口环境：${resolveApiEnv()}（可切 ${API_ENVS.join(
    '/'
  )}，?api=reset 回默认）`
);

export default getServiceBaseURL;
